import { useEffect, useState } from "react";
import { listSales, useListSalesPage, useGetSale, useUpdateSale, useDeleteSale, getListSalesQueryKey, getListSalesPageQueryKey } from "@workspace/api-client-react";
import { receiptEmailMessage, type ReceiptEmailStatus } from "@/lib/receipt-email";
import { useBranch } from "@/context/branch";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Link } from "wouter";
import { Loader2, PlusCircle, Receipt, Download, Printer, Share2, ShoppingCart, MessageCircle, Eye, Pencil, Trash2, AlertTriangle, Mail } from "lucide-react";
import { format } from "date-fns";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function buildReceiptPDF(sale: any, storeName: string, address: string, phone: string, footer: string): jsPDF {
  const W = 80;
  const margin = 5;
  const contentWidth = W - margin * 2;
  const gap = 2;
  const rightWidth = 26;
  const leftWidth = contentWidth - rightWidth - gap;
  const cx = W / 2;
  const measureDoc = new jsPDF({ unit: "mm", format: [W, 1000], orientation: "portrait" });
  const countLines = (text: string, size: number, isBold = false, width = contentWidth) => {
    measureDoc.setFont("Helvetica", isBold ? "bold" : "normal");
    measureDoc.setFontSize(size);
    return measureDoc.splitTextToSize(text, width).length;
  };
  const rowLineCount = (label: string, value: string, size: number, isBold = false) =>
    Math.max(countLines(label, size, isBold, leftWidth), countLines(value, size, isBold, rightWidth));
  const itemLineCounts: number[] = (sale.items ?? []).map((item: any): number =>
    rowLineCount(`${item.productName} x${item.quantity}`, `NGN ${Number(item.total).toFixed(2)}`, 10),
  );
  const addressText = address.replace(/\n/g, ", ");
  const headerLineCounts = [
    countLines(storeName.toUpperCase(), 14, true),
    ...(addressText ? [countLines(addressText, 9)] : []),
    ...(phone ? [countLines(`Tel: ${phone}`, 9)] : []),
    countLines(`Receipt #${sale.receiptNumber}`, 9),
    countLines(format(new Date(sale.createdAt), "MMM d, yyyy  h:mm a"), 9),
    ...(sale.staffName ? [countLines(`Served by: ${sale.staffName}`, 9)] : []),
  ];
  const footerText = footer || "Thank you for your patronage!";
  const headerExtraLines = headerLineCounts.reduce((sum, lines) => sum + Math.max(0, lines - 1), 0);
  const footerExtraLines = Math.max(0, countLines(footerText, 9) - 1);
  const extras = (Number(sale.discount) > 0 ? 1 : 0)
    + (Number(sale.tax) > 0 ? 1 : 0)
    + (Number(sale.balance ?? 0) > 0 ? 1 : 0);
  const estimatedHeight = 90
    + itemLineCounts.reduce((sum, lines) => sum + lines * 5, 0)
    + (headerExtraLines + footerExtraLines) * 5
    + extras * 5;
  const doc = new jsPDF({ unit: "mm", format: [W, estimatedHeight], orientation: "portrait" });

  let y = 9;
  const lh = 5;
  const lhSm = 4;

  const setFont = (sz: number, isBold = false) => {
    doc.setFont("Helvetica", isBold ? "bold" : "normal");
    doc.setFontSize(sz);
  };
  const center = (text: string, yy: number, sz: number, isBold = false) => {
    setFont(sz, isBold);
    const lines = doc.splitTextToSize(text, contentWidth);
    doc.text(lines, cx, yy, { align: "center" });
    return lines.length;
  };
  const row = (label: string, value: string, yy: number, sz: number, isBold = false) => {
    setFont(sz, isBold);
    const labelLines = doc.splitTextToSize(label, leftWidth);
    const valueLines = doc.splitTextToSize(value, rightWidth);
    doc.text(labelLines, margin, yy);
    doc.text(valueLines, W - margin, yy, { align: "right" });
    return Math.max(labelLines.length, valueLines.length);
  };
  const dash = (yy: number) => {
    doc.setDrawColor(160);
    doc.setLineDashPattern([1.5, 1.5], 0);
    doc.line(margin, yy, W - margin, yy);
    doc.setLineDashPattern([], 0);
  };
  const fmt = (n: number) => `NGN ${n.toFixed(2)}`;

  y += center(storeName.toUpperCase(), y, 14, true) * lh + 1;
  if (addressText) y += center(addressText, y, 9) * lhSm;
  if (phone) y += center(`Tel: ${phone}`, y, 9) * lhSm;
  y += center(`Receipt #${sale.receiptNumber}`, y, 9) * lhSm;
  y += center(format(new Date(sale.createdAt), "MMM d, yyyy  h:mm a"), y, 9) * lhSm;
  if (sale.staffName) y += center(`Served by: ${sale.staffName}`, y, 9) * lhSm;

  y += 1; dash(y); y += lh;

  for (const item of sale.items ?? []) {
    const name = `${item.productName} x${item.quantity}`;
    const lines = row(name, fmt(Number(item.total)), y, 10);
    y += lines * lh;
  }

  dash(y); y += lh;

  if (Number(sale.discount ?? 0) > 0) {
    y += row("Discount", `-${fmt(Number(sale.discount))}`, y, 10) * lh;
  }
  if (Number(sale.tax ?? 0) > 0) {
    y += row("Tax", `+${fmt(Number(sale.tax))}`, y, 10) * lh;
  }

  y += row("TOTAL", fmt(Number(sale.totalAmount)), y, 12, true) * lh + 1;

  y += row("Payment", (sale.paymentMethod ?? "").charAt(0).toUpperCase() + (sale.paymentMethod ?? "").slice(1), y, 9) * lhSm;
  if (Number(sale.amountPaid ?? 0) > 0) {
    y += row("Paid", fmt(Number(sale.amountPaid)), y, 9) * lhSm;
  }
  if (Number(sale.balance ?? 0) > 0) {
    y += row("Balance Due", fmt(Number(sale.balance)), y, 9) * lhSm;
  }

  dash(y); y += lh;

  y += center(footerText, y, 9) * lhSm;
  center("Powered by RetailPulse", y, 9);

  return doc;
}

function SaleDetailModal({ saleId, onClose }: { saleId: number; onClose: () => void }) {
  const { data: sale, isLoading } = useGetSale(saleId, {
    query: { enabled: !!saleId, queryKey: ["sale", saleId], refetchInterval: query =>
      query.state.data?.receiptEmailStatus === "sending" ? 10_000 : false },
  });
  const { store } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [sharing, setSharing] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [requestKey, setRequestKey] = useState<string | null>(null);
  const [emailStatus, setEmailStatus] = useState<{ receiptEmailStatus: ReceiptEmailStatus; receiptEmailAttempts: number } | null>(null);

  const sendReceipt = async () => {
    if (sendingEmail) return;
    const acknowledgeUnknown = (emailStatus?.receiptEmailStatus ?? sale?.receiptEmailStatus) === "unknown";
    if (acknowledgeUnknown && !window.confirm("Delivery could not be confirmed. Check with the customer first. Resending may send a duplicate receipt. Continue?")) return;
    setSendingEmail(true);
    const key = requestKey ?? crypto.randomUUID();
    setRequestKey(key);
    try {
      const response = await fetch(`${BASE}/api/sales/${saleId}/email`, {
        method: "POST", credentials: "include", headers: { "Idempotency-Key": key, "Content-Type": "application/json" },
        body: JSON.stringify({ acknowledgeUnknown }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Could not send receipt");
      setEmailStatus(result);
      setRequestKey(null);
      queryClient.invalidateQueries({ queryKey: ["sale", saleId] });
      queryClient.invalidateQueries({ queryKey: getListSalesPageQueryKey() });
      toast({
        title: result.receiptEmailStatus === "accepted" ? "Email server accepted receipt" : "Receipt email not confirmed",
        description: receiptEmailMessage(result.receiptEmailStatus),
        variant: result.receiptEmailStatus === "failed" || result.receiptEmailStatus === "unknown" ? "destructive" : "default",
      });
    } catch (error) {
      toast({ title: "Could not confirm email status", description: error instanceof Error ? error.message : "Please try again.", variant: "destructive" });
    } finally {
      setSendingEmail(false);
    }
  };

  const storeName = store?.name ?? "RetailPulse";
  const address = store?.address ?? "";
  const phone = store?.phone ?? "";
  const footer = store?.receiptFooter ?? "Thank you for your patronage!";

  const printReceipt = () => {
    if (!sale) return;
    const logoUrl = store?.logoUrl ? `${window.location.origin}${BASE}/api/storage${store.logoUrl}` : "";
    const w = window.open("", "_blank", "width=420,height=700");
    if (!w) return;
    w.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Receipt ${sale.receiptNumber}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    @page { size: 80mm auto; margin: 0; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 14px; font-weight: 600; line-height: 1.45; -webkit-font-smoothing: auto; text-rendering: auto; background: #fff; color: #000; width: 70mm; max-width: 70mm; margin: 0 auto; padding: 4mm 0 5mm; }
    .center { text-align: center; }
    .logo { max-width: 80px; max-height: 80px; object-fit: contain; margin: 0 auto 8px; display: block; }
    .store-name { font-size: 20px; font-weight: 800; letter-spacing: .6px; margin-bottom: 2px; overflow-wrap: anywhere; }
    .store-detail { font-size: 13px; font-weight: 600; color: #000; margin-bottom: 2px; overflow-wrap: anywhere; }
    .receipt-no { font-size: 13px; font-weight: 600; color: #000; margin-bottom: 4px; }
    .date { font-size: 13px; font-weight: 600; color: #000; overflow-wrap: anywhere; }
    .divider { border: none; border-top: 1px dashed #444; margin: 10px 0; }
    .items { margin: 8px 0; font-size: 14px; }
    .item { display: flex; align-items: flex-start; justify-content: space-between; gap: 4px; margin-bottom: 6px; }
    .item-name { flex: 1; min-width: 0; padding-right: 4px; overflow-wrap: anywhere; font-weight: 600; }
    .item > span:last-child { flex-shrink: 0; white-space: nowrap; text-align: right; }
    .summary { margin-top: 4px; }
    .summary-row, .total-row, .payment { display: flex; justify-content: space-between; gap: 4px; }
    .summary-row { margin-bottom: 4px; font-size: 13px; }
    .total-row { font-weight: 700; font-size: 17px; margin-top: 6px; padding-top: 6px; border-top: 1px solid #000; }
    .payment { margin-top: 8px; font-size: 13px; font-weight: 600; color: #000; }
    .summary-row > span:last-child, .total-row > span:last-child, .payment > span:last-child { flex-shrink: 0; white-space: nowrap; text-align: right; }
    .footer { text-align: center; margin-top: 16px; font-size: 13px; font-weight: 600; color: #000; line-height: 1.5; overflow-wrap: anywhere; }
    @media print {
      .no-print { display: none !important; }
      * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      body { width: 70mm; max-width: 70mm; margin: 0 auto; color: #000 !important; font-weight: 600; -webkit-font-smoothing: auto; }
    }
  </style>
</head>
<body>
  <div class="center">
    ${logoUrl ? `<img src="${logoUrl}" class="logo" alt="logo" crossorigin="anonymous" />` : ""}
    <div class="store-name">${storeName.toUpperCase()}</div>
    ${address ? `<div class="store-detail">${address.replace(/\n/g, ", ")}</div>` : ""}
    ${phone ? `<div class="store-detail">Tel: ${phone}</div>` : ""}
    <div class="receipt-no" style="margin-top:6px;">Receipt #${sale.receiptNumber}</div>
    <div class="date">${format(new Date(sale.createdAt), "MMM d, yyyy  h:mm a")}</div>
    ${sale.staffName ? `<div class="date">Served by: ${sale.staffName}</div>` : ""}
  </div>
  <hr class="divider" />
  <div class="items">
    ${(sale.items ?? []).map(item => `
      <div class="item">
        <span class="item-name">${item.productName} x${item.quantity}</span>
        <span>&#x20A6;${Number(item.total).toFixed(2)}</span>
      </div>
    `).join("")}
  </div>
  <hr class="divider" />
  <div class="summary">
    ${(sale.discount ?? 0) > 0 ? `<div class="summary-row"><span>Discount</span><span>-&#x20A6;${Number(sale.discount ?? 0).toFixed(2)}</span></div>` : ""}
    ${(sale.tax ?? 0) > 0 ? `<div class="summary-row"><span>Tax</span><span>+&#x20A6;${Number(sale.tax ?? 0).toFixed(2)}</span></div>` : ""}
    <div class="total-row"><span>TOTAL</span><span>&#x20A6;${Number(sale.totalAmount).toFixed(2)}</span></div>
  </div>
  <div class="payment"><span>Payment Method</span><span style="text-transform:capitalize;">${sale.paymentMethod}</span></div>
  ${Number(sale.balance ?? 0) > 0 ? `<div class="payment"><span>Balance Due</span><span>&#x20A6;${Number(sale.balance).toFixed(2)}</span></div>` : ""}
  <hr class="divider" />
  <div class="footer">${footer}<br />Powered by RetailPulse</div>
  <br />
  <div class="no-print" style="text-align:center;margin-top:12px;">
    <button onclick="window.print();window.close();" style="padding:8px 20px;background:#000;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">🖨 Print Receipt</button>
  </div>
</body>
</html>`);
    w.document.close();
    setTimeout(() => w.print(), 600);
  };

  const handleDownload = () => {
    if (!sale) return;
    const doc = buildReceiptPDF(sale, storeName, address, phone, footer);
    doc.save(`receipt-${sale.receiptNumber}.pdf`);
  };

  const handleShare = async () => {
    if (!sale || sharing) return;
    setSharing(true);
    try {
      const doc = buildReceiptPDF(sale, storeName, address, phone, footer);
      const blob = doc.output("blob");
      const file = new File([blob], `receipt-${sale.receiptNumber}.pdf`, { type: "application/pdf" });

      if (typeof navigator.share === "function" && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `Receipt ${sale.receiptNumber}`,
          text: `${storeName} — ${format(new Date(sale.createdAt), "MMM d, yyyy")}`,
        });
      } else {
        const total = `NGN ${Number(sale.totalAmount).toFixed(2)}`;
        const text = `*Receipt from ${storeName}*\nReceipt #: ${sale.receiptNumber}\nDate: ${format(new Date(sale.createdAt), "MMM d, yyyy")}\nTotal: ${total}`;
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
      }
    } catch {
      // user cancelled share — no-op
    } finally {
      setSharing(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg w-full mx-2 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg">
            Receipt {sale?.receiptNumber}
          </DialogTitle>
          {sale && (
            <div className="flex flex-wrap gap-2 pt-1">
              <Button size="sm" variant="outline" className="gap-1.5" onClick={printReceipt}>
                <Printer className="w-3.5 h-3.5" /> Print
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5" onClick={handleDownload}>
                <Download className="w-3.5 h-3.5" /> Download PDF
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5" onClick={handleShare} disabled={sharing}>
                {sharing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
                Share
              </Button>
              {sale && (
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                  onClick={() => {
                    const items = (sale.items ?? []).map(i => `• ${i.productName} x${i.quantity} = ₦${Number(i.total).toFixed(2)}`).join("\n");
                    const text = `*Receipt from ${storeName}*\nReceipt #${sale.receiptNumber}\nDate: ${format(new Date(sale.createdAt), "MMM d, yyyy h:mm a")}\n\n${items}\n\n*Total: ₦${Number(sale.totalAmount).toFixed(2)}*\nPayment: ${sale.paymentMethod}\n\nThank you! 🛒`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
                  }}
                >
                  <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                </Button>
              )}
            </div>
          )}
        </DialogHeader>
        {isLoading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : sale ? (
          <div className="space-y-4">
            <div className="flex justify-between text-sm text-muted-foreground flex-wrap gap-1">
              <span>{format(new Date(sale.createdAt), "MMM d, yyyy h:mm a")}</span>
              <span>{sale.staffName || "No staff assigned"}</span>
            </div>
            <Separator />
            <div className="space-y-2">
              {sale.items?.map((item, i) => (
                <div key={i} className="flex justify-between text-sm gap-2">
                  <span className="flex-1">{item.productName} x{item.quantity}</span>
                  <span className="shrink-0">₦{Number(item.total).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <Separator />
            <div className="space-y-1 text-sm">
              {(sale.discount ?? 0) > 0 && (
                <div className="flex justify-between"><span>Discount</span><span>-₦{Number(sale.discount ?? 0).toFixed(2)}</span></div>
              )}
              {(sale.tax ?? 0) > 0 && (
                <div className="flex justify-between"><span>Tax</span><span>+₦{Number(sale.tax ?? 0).toFixed(2)}</span></div>
              )}
              <div className="flex justify-between font-bold text-base pt-1">
                <span>Total</span>
                <span>₦{Number(sale.totalAmount).toFixed(2)}</span>
              </div>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Payment</span>
              <Badge variant="secondary" className="capitalize">{sale.paymentMethod}</Badge>
            </div>
            <div className="rounded-md border p-3 text-sm space-y-2">
              <p className="font-medium">Receipt email · {emailStatus?.receiptEmailAttempts ?? sale.receiptEmailAttempts ?? 0} attempt(s)</p>
              <p className="text-muted-foreground">{receiptEmailMessage(emailStatus?.receiptEmailStatus ?? sale.receiptEmailStatus)}</p>
              {sale.canEmailReceipt && (
                <Button size="sm" variant="outline" onClick={sendReceipt} disabled={sendingEmail ||
                  (emailStatus?.receiptEmailStatus ?? sale.receiptEmailStatus) === "sending"}>
                  {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                  {["accepted", "unknown"].includes(emailStatus?.receiptEmailStatus ?? sale.receiptEmailStatus ?? "") ? "Resend receipt" : "Send receipt"}
                </Button>
              )}
            </div>
            {Number(sale.amountPaid ?? 0) > 0 && Number(sale.balance ?? 0) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Balance Due</span>
                <span className="font-medium text-orange-600">₦{Number(sale.balance).toFixed(2)}</span>
              </div>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

// ----- Edit Sale Dialog -----
function EditSaleDialog({ sale, onClose }: { sale: any; onClose: () => void }) {
  const queryClient = useQueryClient();
  const updateSale = useUpdateSale();
  const [notes, setNotes] = useState<string>(sale.notes ?? "");
  const [paymentMethod, setPaymentMethod] = useState<string>(sale.paymentMethod ?? "cash");
  const [status, setStatus] = useState<string>(sale.status ?? "completed");
  const [discount, setDiscount] = useState<string>(String(Number(sale.discount ?? 0)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const protectsCustomerHistory = Boolean(sale.customerId);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await updateSale.mutateAsync({
        id: sale.id,
        data: {
          notes: notes || null,
          ...(!protectsCustomerHistory ? {
            paymentMethod,
            status,
            discount: parseFloat(discount) || 0,
          } : {}),
        },
      });
      queryClient.invalidateQueries({ queryKey: getListSalesQueryKey() });
      queryClient.invalidateQueries({ queryKey: getListSalesPageQueryKey() });
      queryClient.invalidateQueries({ queryKey: ["sale", sale.id] });
      onClose();
    } catch (e: any) {
      setError(e?.message ?? "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Sale — {sale.receiptNumber}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {protectsCustomerHistory && (
            <p className="rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground">
              This sale is linked to a customer. To preserve their payment and loyalty history, only notes can be edited here. Record credit payments or returns separately.
            </p>
          )}
          {!protectsCustomerHistory && (<>
          <div className="space-y-1.5">
            <Label>Payment Method</Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="transfer">Transfer</SelectItem>
                <SelectItem value="credit">Credit</SelectItem>
                <SelectItem value="mixed">Mixed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Discount (₦)</Label>
            <Input
              type="number"
              min={0}
              step="0.01"
              value={discount}
              onChange={e => setDiscount(e.target.value)}
            />
          </div>
          </>)}
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add a note..."
              rows={3}
            />
          </div>
          {error && (
            <p className="text-sm text-destructive flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----- Delete Confirm Dialog -----
function DeleteSaleDialog({ sale, onClose, onDeleted }: { sale: any; onClose: () => void; onDeleted: () => void }) {
  const queryClient = useQueryClient();
  const deleteSale = useDeleteSale();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await deleteSale.mutateAsync({ id: sale.id });
      queryClient.invalidateQueries({ queryKey: getListSalesQueryKey() });
      queryClient.invalidateQueries({ queryKey: getListSalesPageQueryKey() });
      onDeleted();
    } catch (e: any) {
      setError(e?.message ?? "Delete failed. Please try again.");
      setDeleting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="w-5 h-5" /> Delete Sale
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          {sale.customerId ? (
            <p className="text-sm">Sale <strong>{sale.receiptNumber}</strong> is linked to a customer and cannot be deleted without losing their payment and loyalty history. Record a return instead.</p>
          ) : (<>
          <p className="text-sm">Are you sure you want to delete <strong>{sale.receiptNumber}</strong>?</p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 space-y-1">
            <p className="font-medium">This will:</p>
            <ul className="list-disc list-inside space-y-0.5 text-amber-700">
              <li>Permanently delete the sale record</li>
              <li>Restore stock for all items sold</li>
            </ul>
          </div>
          </>)}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting}>Cancel</Button>
          {!sale.customerId && <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
            {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting…</> : "Yes, Delete"}
          </Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Sales() {
  const { activeBranchId, activeBranch } = useBranch();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  const isAdminOrManager = isAdmin || user?.role === "manager";

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [editingSale, setEditingSale] = useState<any>(null);
  const [deletingSale, setDeletingSale] = useState<any>(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const { toast } = useToast();

  const exportCSV = async () => {
    setExporting(true);
    try {
      const salesData = await listSales({
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        branchId: activeBranchId ?? undefined,
      });
      const rows = [...salesData].reverse().map(s => ({
        "Receipt #": s.receiptNumber,
        "Date": format(new Date(s.createdAt), "MMM d, yyyy h:mm a"),
        "Cashier": s.staffName || "",
        "Payment Method": s.paymentMethod,
        "Status": s.status,
        "Total (NGN)": Number(s.totalAmount).toFixed(2),
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sales");
      const date = new Date().toISOString().split("T")[0];
      XLSX.writeFile(wb, `sales_${date}.csv`, { bookType: "csv" });
    } catch {
      toast({ title: "Could not export sales", description: "Check your connection and try again.", variant: "destructive" });
    } finally {
      setExporting(false);
    }
  };

  const { data, isLoading } = useListSalesPage({
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    branchId: activeBranchId ?? undefined,
    page,
    pageSize: 20,
  });
  const sales = data?.sales;
  const totalPages = data?.totalPages ?? 0;

  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, activeBranchId]);

  useEffect(() => {
    if (data && page > Math.max(1, data.totalPages)) setPage(Math.max(1, data.totalPages));
  }, [data, page]);

  const paymentBadge: Record<string, "default" | "secondary" | "outline"> = {
    cash: "default",
    card: "secondary",
    transfer: "outline",
  };

  return (
    <div className="space-y-6">
      {selectedId && <SaleDetailModal saleId={selectedId} onClose={() => setSelectedId(null)} />}
      {editingSale && <EditSaleDialog sale={editingSale} onClose={() => setEditingSale(null)} />}
      {deletingSale && (
        <DeleteSaleDialog
          sale={deletingSale}
          onClose={() => setDeletingSale(null)}
          onDeleted={() => setDeletingSale(null)}
        />
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sales</h1>
          <p className="text-muted-foreground mt-1">
            {activeBranch ? `Viewing transactions for ${activeBranch.name}.` : "Viewing transactions across all locations."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {data && data.total > 0 && (
            <Button variant="outline" onClick={exportCSV} disabled={exporting}>
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {exporting ? "Exporting…" : "Export CSV"}
            </Button>
          )}
          <Link href="/sales/new">
            <Button><PlusCircle className="w-4 h-4 mr-2" /> New Sale</Button>
          </Link>
        </div>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-muted-foreground">From</label>
          <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-40" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-muted-foreground">To</label>
          <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-40" />
        </div>
        {(startDate || endDate) && (
          <Button variant="ghost" onClick={() => { setStartDate(""); setEndDate(""); }}>Clear</Button>
        )}
      </div>

      <div className="border rounded-md overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Receipt #</TableHead>
              <TableHead className="hidden sm:table-cell">Date</TableHead>
              <TableHead className="hidden md:table-cell">Customer</TableHead>
              <TableHead className="hidden md:table-cell">Cashier</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead className="hidden sm:table-cell">Status</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className={isAdminOrManager ? "w-[100px]" : "w-[60px]"}>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></TableCell></TableRow>
            ) : data?.total === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="py-16">
                  <div className="flex flex-col items-center gap-3 text-center">
                    <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                      <Receipt className="w-6 h-6 text-muted-foreground/50" />
                    </div>
                    <div>
                      <p className="font-semibold">No sales yet</p>
                      <p className="text-sm text-muted-foreground mt-0.5">Record your first sale to start tracking revenue.</p>
                    </div>
                    <Link href="/sales/new">
                      <Button size="sm" className="gap-2 mt-1">
                        <ShoppingCart className="w-4 h-4" /> Make Your First Sale
                      </Button>
                    </Link>
                    <p className="text-xs text-muted-foreground/60">💡 You need products in inventory before making a sale</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (sales || []).map(s => (
              <TableRow key={s.id} className="cursor-pointer" onClick={() => setSelectedId(s.id)}>
                <TableCell className="font-mono text-sm">{s.receiptNumber}</TableCell>
                <TableCell className="hidden sm:table-cell text-sm">{format(new Date(s.createdAt), "MMM d, yyyy h:mm a")}</TableCell>
                <TableCell className="hidden md:table-cell">{s.customerName || <span className="text-muted-foreground text-xs">Walk-in</span>}</TableCell>
                <TableCell className="hidden md:table-cell">{s.staffName || "—"}</TableCell>
                <TableCell><Badge variant={paymentBadge[s.paymentMethod] || "outline"}>{s.paymentMethod}</Badge></TableCell>
                <TableCell className="hidden sm:table-cell"><Badge variant={s.status === "completed" ? "default" : "secondary"}>{s.status}</Badge></TableCell>
                <TableCell className="text-right font-medium">₦{s.totalAmount.toFixed(2)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-0.5" onClick={e => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" title="View receipt" onClick={() => setSelectedId(s.id)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    {isAdminOrManager && (
                      <Button variant="ghost" size="icon" title="Edit sale" onClick={() => setEditingSale(s)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    {isAdmin && (
                      <Button variant="ghost" size="icon" title="Delete sale" className="text-destructive hover:text-destructive" onClick={() => setDeletingSale(s)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {data && data.total > 0 && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * data.pageSize + 1}–{Math.min(page * data.pageSize, data.total)} of {data.total} sales
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1 || isLoading}>
              Previous
            </Button>
            <span className="text-sm tabular-nums">Page {page} of {Math.max(1, totalPages)}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages || isLoading}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
